import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { formatHours, percentOf, sharePercent } from '../charts/chart-theme';
import { StackedCategoryBar, type StackedRow } from '../charts/stacked-category-bar';
import {
  ChartTableToggle,
  type ChartTableView,
  type UnitMode,
  UnitToggle,
} from '../segmented-toggle';
import { WidgetCard, type WidgetInsights } from '../widget-card';
import { WidgetLegend } from '../widget-legend';
import { type WidgetColumn, WidgetTable } from '../widget-table';
import { CATEGORIES, committedHours, type Group } from './fixtures';

const LEGEND = CATEGORIES.map((c) => ({ color: c.color, label: c.label }));

/**
 * How one grouping (location or segment) spent its available hours, split by
 * where the hours went rather than by billability.
 *
 * One tile per grouping. They were a single tile with a Location/Segment
 * switcher, and were split apart so both can be read at once — comparing
 * "where" against "what kind of work" is the point.
 */
export function MixTile({
  title,
  groups,
  groupLabel,
  insights,
  onDrill,
}: {
  title: string;
  groups: Group[];
  /** Noun for the grouping, e.g. "Location". */
  groupLabel: string;
  insights: WidgetInsights;
  onDrill: (group: Group) => void;
}) {
  const [unit, setUnit] = useState<UnitMode>('percent');
  const [view, setView] = useState<ChartTableView>('chart');
  const asHours = unit === 'hours';

  // Seeded with 0 so an empty group list can't yield -Infinity.
  const maxAvailable = Math.max(0, ...groups.map((g) => g.available));

  const rows: StackedRow[] = groups.map((g) => {
    const values: Record<string, number> = {};
    for (const c of CATEGORIES) {
      const raw = Math.max(0, g[c.key]);
      values[c.key] = asHours ? raw : sharePercent(raw, g.available);
    }
    return {
      name: g.name,
      caption: `${g.status} · ${g.people} ppl`,
      total: asHours
        ? formatHours(committedHours(g))
        : `${percentOf(committedHours(g), g.available)}%`,
      values,
    };
  });

  // Memoised on the values it actually reads: WidgetTable memoises its column
  // defs on this array's identity, and TanStack rebuilds its row models
  // whenever those defs change. A fresh array each render defeated both.
  const columns: WidgetColumn<Group>[] = useMemo(() => {
    const cell = (value: number, whole: number): string =>
      asHours ? formatHours(value) : `${percentOf(value, whole)}%`;

    return [
      {
        key: 'name',
        header: `${groupLabel} & status`,
        render: (g) => (
          <span className="flex items-center gap-2">
            {g.name}
            <Badge variant="outline">{g.status}</Badge>
          </span>
        ),
        sortValue: (g) => g.name,
      },
      {
        key: 'people',
        header: 'People',
        numeric: true,
        render: (g) => g.people,
        sortValue: (g) => g.people,
      },
      {
        key: 'available',
        header: 'Available hrs',
        numeric: true,
        render: (g) => formatHours(g.available),
        sortValue: (g) => g.available,
      },
      ...CATEGORIES.filter((c) => c.key !== 'leave' && c.key !== 'idle').map((c) => ({
        key: c.key,
        header: c.label,
        numeric: true,
        render: (g: Group) => cell(g[c.key], g.available),
        sortValue: (g: Group) => g[c.key],
      })),
      {
        key: 'total',
        header: asHours ? 'Committed' : 'Overall',
        numeric: true,
        render: (g: Group) => (
          <b>
            {asHours
              ? formatHours(committedHours(g))
              : `${percentOf(committedHours(g), g.available)}%`}
          </b>
        ),
        // Guarded: a zero denominator would make the sort order arbitrary.
        sortValue: (g: Group) => sharePercent(committedHours(g), g.available),
      },
    ];
  }, [groupLabel, asHours]);

  return (
    <WidgetCard
      title={title}
      subtitle={
        asHours
          ? `Hours by ${groupLabel.toLowerCase()} · click a row to drill into its people`
          : 'Share of net-available hours · click a row to drill into its people'
      }
      controls={
        <>
          <UnitToggle value={unit} onChange={setUnit} />
          <ChartTableToggle value={view} onChange={setView} />
        </>
      }
      insights={insights}
    >
      {view === 'chart' ? (
        <>
          <StackedCategoryBar
            rows={rows}
            series={CATEGORIES.map((c) => ({ key: c.key, label: c.label, color: c.color }))}
            max={asHours ? maxAvailable : 100}
            onRowClick={(i) => {
              const group = groups[i];
              if (group) onDrill(group);
            }}
          />
          <WidgetLegend items={LEGEND} />
        </>
      ) : (
        <WidgetTable
          rows={groups}
          columns={columns}
          defaultSortKey="available"
          onRowClick={onDrill}
        />
      )}
    </WidgetCard>
  );
}
